import { motion } from 'motion/react';
import { ArrowRight, MapPin, Briefcase, Users, Target, Coffee, Eye, Zap, Search, Filter, Calendar, DollarSign, X, ChevronDown } from 'lucide-react';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ImageWithFallback } from '../../components/figma/ImageWithFallback';
import { useJobs } from '../contexts/JobsContext';
import { filterActiveJobs, formatPostedDate } from '../../types/job';
import { JobExpirationCountdown } from '../JobExpirationCountdown';
import { ResumeModal } from '../ResumeModal';

function JobCard({ id, title, department, location, workMode, type, experience, salary, postedDate, expirationDate, description, delay = 0 }: {
  id?: string;
  title: string;
  department: string;
  location: string;
  workMode?: string;
  type: string;
  experience?: string;
  salary?: string;
  postedDate?: string;
  expirationDate?: string;
  description: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="h-full"
    >
      <Link to={id ? `/careers/${id}` : '#'} className="block h-full">
        <Card className="glass p-6 h-full hover:border-primary/50 transition-all duration-300 cursor-pointer group">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-lg group-hover:text-primary transition-colors duration-300 flex-1">{title}</h3>
                {expirationDate && (
                  <JobExpirationCountdown
                    expirationDate={expirationDate}
                    className="ml-2 flex-shrink-0"
                    showIcon={false}
                  />
                )}
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant="outline" className="border-primary/50 text-primary">{department}</Badge>
                <Badge variant="outline" className="border-accent/50 text-accent">{type}</Badge>
                {workMode && (
                  <Badge variant="outline" className="border-secondary/50 text-secondary">{workMode}</Badge>
                )}
                {experience && (
                  <Badge variant="outline" className="border-muted-foreground/50 text-muted-foreground">{experience}</Badge>
                )}
              </div>
            </div>
          </div>

          <p className="text-muted-foreground mb-4 leading-relaxed line-clamp-3">{description}</p>

          <div className="space-y-2 mb-4">
            <div className="flex items-center text-muted-foreground text-sm">
              <MapPin className="w-4 h-4 mr-2" />
              {location}
            </div>
            {salary && (
              <div className="flex items-center text-muted-foreground text-sm">
                <DollarSign className="w-4 h-4 mr-2" />
                {salary}
              </div>
            )}
            {postedDate && (
              <div className="flex items-center text-muted-foreground text-sm">
                <Calendar className="w-4 h-4 mr-2" />
                Posted {formatPostedDate(postedDate)}
              </div>
            )}
          </div>

          <Button className="w-full rounded-full bg-primary text-primary-foreground hover:neon-glow group-hover:scale-105 transition-all duration-300">
            View Details
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Button>
        </Card>
      </Link>
    </motion.div>
  );
}

export function CareersPage() {
  const { jobs, isLoading } = useJobs();
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [workModeFilter, setWorkModeFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);

  // First filter out expired jobs and only show active/published jobs, then get unique filter values
  const activeJobs = useMemo(() => {
    const publishedJobs = jobs.filter(job => job.status === 'active');
    return filterActiveJobs(publishedJobs);
  }, [jobs]);

  const departments = [...new Set(activeJobs.map(job => job.department))];
  const locations = [...new Set(activeJobs.map(job => job.location))];
  const workModes = [...new Set(activeJobs.map(job => job.workMode).filter((mode): mode is string => !!mode))];
  const types = [...new Set(activeJobs.map(job => job.type))];

  // Filter active jobs based on search and filters
  const filteredJobs = useMemo(() => {
    return activeJobs.filter(job => {
      const matchesSearch = searchQuery === '' ||
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.department.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesDepartment = !departmentFilter || job.department === departmentFilter;
      const matchesLocation = !locationFilter || job.location === locationFilter;
      const matchesWorkMode = !workModeFilter || job.workMode === workModeFilter;
      const matchesType = !typeFilter || job.type === typeFilter;

      return matchesSearch && matchesDepartment && matchesLocation && matchesWorkMode && matchesType;
    });
  }, [activeJobs, searchQuery, departmentFilter, locationFilter, workModeFilter, typeFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setDepartmentFilter('');
    setLocationFilter('');
    setWorkModeFilter('');
    setTypeFilter('');
  };

  const hasActiveFilters = searchQuery || departmentFilter || locationFilter || workModeFilter || typeFilter;

  const benefits = [
    { icon: <Target className="w-6 h-6" />, title: "Competitive Salary", description: "Industry-leading compensation packages" },
    { icon: <Coffee className="w-6 h-6" />, title: "Flexible Work", description: "Remote-first culture with flexible hours" },
    { icon: <Zap className="w-6 h-6" />, title: "Growth Opportunities", description: "Continuous learning and career development" },
    { icon: <Users className="w-6 h-6" />, title: "Great Team", description: "Work with world-class AI experts" }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-5 bg-gradient-to-b from-background to-background/50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#00cfff" fill-opacity="0.03"><path d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/></g></g></svg>')}")`
        }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <Briefcase className="w-12 h-12 text-primary mr-4" />
              <span className="text-primary font-semibold">Join the AI Revolution</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 neon-text">
              Shape the Future of AI
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground text-center mb-8">
              Join our world-class team of engineers, researchers, and innovators
              building the next generation of artificial intelligence solutions.
            </p>

            {/* Scroll to Open Positions Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Button
                onClick={() => {
                  document.getElementById('open-positions')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }}
                className="group bg-gradient-to-r from-primary to-accent hover:from-primary/80 hover:to-accent/80 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-primary/50 transition-all duration-300"
              >
                View Open Positions
                <ChevronDown className="ml-2 w-5 h-5 group-hover:translate-y-1 transition-transform" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Open Roles */}
      <section id="open-positions" className="pt-8 pb-15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Open Positions</h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto mb-8">
              We're always looking for talented individuals who share our passion for AI innovation
            </p>
          </motion.div>

          {/* Search and Filters */}
          <div className="max-w-6xl mx-auto mb-12">
            <div className="glass rounded-lg p-6">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
                {/* Search */}
                <div className="lg:col-span-2 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search jobs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-input-background border-border/50 focus:border-primary"
                  />
                </div>

                {/* Department Filter */}
                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                  <SelectTrigger className="bg-input-background border-border/50 focus:border-primary">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent className="dropdown-bg">
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Location Filter */}
                <Select value={locationFilter} onValueChange={setLocationFilter}>
                  <SelectTrigger className="bg-input-background border-border/50 focus:border-primary">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent className="dropdown-bg">
                    {locations.map((location) => (
                      <SelectItem key={location} value={location}>{location}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Work Mode Filter */}
                <Select value={workModeFilter} onValueChange={setWorkModeFilter}>
                  <SelectTrigger className="bg-input-background border-border/50 focus:border-primary">
                    <SelectValue placeholder="Work Mode" />
                  </SelectTrigger>
                  <SelectContent className="dropdown-bg">
                    {workModes.map((mode) => (
                      <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Employment Type Filter */}
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="bg-input-background border-border/50 focus:border-primary">
                    <SelectValue placeholder="Employment Type" />
                  </SelectTrigger>
                  <SelectContent className="dropdown-bg">
                    {types.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Filter className="w-4 h-4 mr-2" />
                    {filteredJobs.length} {filteredJobs.length === 1 ? 'position' : 'positions'} found
                  </div>

                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Clear Filters
                    </Button>
                  )}
                </div>

                {/* Quick Department Tags */}
                <div className="flex flex-wrap gap-2">
                  {departments.slice(1, 5).map((dept) => (
                    <Button
                      key={dept}
                      variant={departmentFilter === dept ? "default" : "outline"}
                      size="sm"
                      onClick={() => setDepartmentFilter(dept)}
                      className={departmentFilter === dept
                        ? "bg-primary text-primary-foreground"
                        : "border-primary/50 hover:border-primary hover:bg-primary/10"
                      }
                    >
                      {dept}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* No Results */}
          {filteredJobs.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center py-12"
            >
              <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">All Roles Filled! 🎉</h3>
              <p className="text-muted-foreground text-center mb-6">
                Currently, all roles are filled with amazing talent.
                Stay tuned for upcoming opportunities!
              </p>
            </motion.div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p className="text-muted-foreground text-lg">Loading career opportunities...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredJobs.map((job, index) => (
                <JobCard
                  key={job.id}
                  {...job}
                  delay={index * 0.1}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Benefits & Culture */}
      <section className="py-15 bg-gradient-to-b from-background/50 to-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Why Join <span className="bg-gradient-to-r from-white to-white bg-clip-text font-bold text-transparent amasqis-text"> <span className="amasqis-text">amasQIS.ai</span>?</span></h2>
            <p className="text-xl text-muted-foreground text-center max-w-3xl mx-auto">
              We offer more than just a job – we provide an opportunity to shape the future of AI
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <div className="text-primary">{benefit.icon}</div>
                </div>
                <h3 className="font-bold mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Culture Snippets */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Innovation First",
                description: "We encourage experimentation and bold ideas",
                image: "/assets/careers/innovationFirst.png"
              },
              {
                title: "Collaborative Environment",
                description: "Cross-functional teams working towards common goals",
                image: "/assets/careers/collaborativeEnvironment.png"
              }
            ].map((snippet, index) => (
              <motion.div
                key={snippet.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <Card className="glass overflow-hidden hover:border-primary/50 transition-all duration-300">
                  <div className="relative">
                    <ImageWithFallback
                      src={snippet.image}
                      alt={snippet.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <h3 className="font-bold text-lg mb-1">{snippet.title}</h3>
                      <p className="text-sm text-muted-foreground">{snippet.description}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-15 bg-gradient-to-r from-primary/10 to-secondary/10 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">Ready to Join Our Team?</h2>
            <p className="text-xl text-muted-foreground mb-8 text-center max-w-3xl mx-auto">
              Don't see a perfect match? We're always interested in connecting with talented individuals
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setIsResumeModalOpen(true)}
                className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-4 rounded-xl text-white font-semibold hover:shadow-2xl hover:shadow-cyan-500/25 transition-all duration-300 h-auto"
              >
                Send Us Your Resume
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-3 glass px-8 py-4 rounded-xl text-white font-semibold hover:glass-strong transition-all duration-300"
              >
                Learn About Our Culture
                <Eye className="w-5 h-5" />
              </Link>

            </div>
          </motion.div>
        </div>
      </section>

      {/* Resume Modal */}
      <ResumeModal
        isOpen={isResumeModalOpen}
        onClose={() => setIsResumeModalOpen(false)}
      />
    </div>
  );
}